import React, { FC } from "react";
import {
  Modal,
  notification,
  Switch,
  Select,
  Input,
  DatePicker,
  TimePicker,
} from "antd";
import Title from "antd/es/typography/Title";
import { Colors } from "shared/themes";
import { handleRequestError } from "shared/helpers";
import { useCreateReportSchedulesMutation } from "shared/api";
import { ModalSizeValue } from "shared/constants";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";

interface AddReportScheduleModalProps {
  isOpen: boolean;
  closeModal: () => void;
  recipientOptions: { value: string }[];
  formattedReportsListValue?:
    | {
        label: string;
        value: number;
      }[]
    | undefined;
}

const validationSchema = Yup.object({
  report: Yup.number().required("Report is required"),
  recipients: Yup.array()
    .of(Yup.string().email("Invalid email"))
    .min(1, "At least one recipient required")
    .required(),
  email_subject: Yup.string().required("Email subject is required"),
});

const AddReportScheduleModal: FC<AddReportScheduleModalProps> = ({
  isOpen,
  closeModal,
  recipientOptions,
  formattedReportsListValue,
}) => {
  const [createReportSchedule] = useCreateReportSchedulesMutation();
  const [api, contextHolder] = notification.useNotification();

  const storedTimeZone =
    typeof window !== "undefined"
      ? localStorage.getItem("timezone") || dayjs.tz.guess()
      : dayjs.tz.guess();

  const initialValues = {
    recipients: [] as string[],
    email_subject: "",
    email_body: "",
    is_recurrent: true,
    periodicity: "daily",
    time: null as string | null,
    start_periodicity: null as string | null,
    end_periodicity: null as string | null,
    enabled: true,
    report: undefined as number | undefined,
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const { recipients, time, start_periodicity, end_periodicity } = values;

    const trimmedRecipients = recipients.map((email) => email.trim());
    const uniqueRecipients = new Set(trimmedRecipients);

    if (uniqueRecipients.size !== trimmedRecipients.length) {
      api.error({
        message: "Validation",
        description: "Duplicate emails are not allowed",
      });
      return;
    }

    try {
      await createReportSchedule({
        recipients: trimmedRecipients,
        email_body: values.email_body,
        email_subject: values.email_subject,
        is_recurrent: values.is_recurrent,
        periodicity: values.periodicity,
        time: time
          ? dayjs
              .tz(`${dayjs().format("YYYY-MM-DD")}T${time}`, storedTimeZone)
              .utc()
              .format("HH:mm")
          : null,
        start_periodicity: start_periodicity
          ? dayjs(start_periodicity).utc().toISOString()
          : null,
        end_periodicity: end_periodicity
          ? dayjs(end_periodicity).utc().toISOString()
          : null,
        enabled: values.enabled,
        report: values.report!,
      }).unwrap();

      api.success({
        message: "Success",
        description: "Report schedule created successfully",
      });

      closeModal();
    } catch (error) {
      const description = handleRequestError(
        error,
        "Error while creating schedule"
      );
      api.error({ message: "Error", description });
    }
  };

  return (
    <>
      {contextHolder}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched, handleSubmit }) => (
          <Modal
            title={
              <div className="flex max-w-[95%] items-center justify-between w-full flex-wrap gap-2">
                <span>Add Report Schedule</span>
                <div className="flex gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Recurrent</span>
                    <Switch
                      checked={values.is_recurrent}
                      onChange={(checked) =>
                        setFieldValue("is_recurrent", checked)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Enabled</span>
                    <Switch
                      checked={values.enabled}
                      onChange={(checked) => setFieldValue("enabled", checked)}
                    />
                  </div>
                </div>
              </div>
            }
            open={isOpen}
            onCancel={closeModal}
            onOk={() => handleSubmit()}
            okButtonProps={{
              style: { background: Colors.blue.primary },
            }}
            width={ModalSizeValue.PRIMARY}
          >
            <Form>
              {/* Report */}
              <Title level={5} className="text-[14px] mt-4">
                Report
              </Title>
              <Select
                style={{ width: "100%" }}
                placeholder="Select report"
                value={values.report}
                onChange={(v) => setFieldValue("report", v)}
                options={formattedReportsListValue || []}
              />
              {touched.report && errors.report && (
                <p className="text-red-500 text-sm mt-1">{errors.report}</p>
              )}

              {/* Recipients */}
              <Title level={5} className="text-[14px] mt-4">
                Recipients
              </Title>
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="Add or select recipients"
                value={values.recipients}
                onChange={(vals) => setFieldValue("recipients", vals)}
                options={recipientOptions.map((item) => ({
                  label: item.value,
                  value: item.value,
                }))}
              />
              {touched.recipients && errors.recipients && (
                <p className="text-red-500 text-sm mt-1">{`${errors.recipients}`}</p>
              )}

              {/* Periodicity */}
              <Title level={5} className="text-[14px] mt-4">
                Periodicity
              </Title>
              <Select
                value={values.periodicity}
                onChange={(v) => setFieldValue("periodicity", v)}
                style={{ width: "100%" }}
              >
                <Select.Option value="daily">Daily</Select.Option>
                <Select.Option value="weekly">Weekly</Select.Option>
                <Select.Option value="monthly">Monthly</Select.Option>
                <Select.Option value="yearly">Yearly</Select.Option>
              </Select>

              {/* Time */}
              <Title level={5} className="text-[14px] mt-4">
                Time
              </Title>
              <TimePicker
                allowClear
                style={{ width: "100%" }}
                format="HH:mm"
                value={values.time ? dayjs(`1970-01-01T${values.time}`) : null}
                onChange={(val) =>
                  setFieldValue(
                    "time",
                    val ? dayjs(val).format("HH:mm:ss") : null
                  )
                }
              />

              {/* Start + End Periodicity */}
              <div className="flex gap-4 mt-4">
                <div className="w-1/2">
                  <Title level={5} className="text-[14px]">
                    Start Periodicity
                  </Title>
                  <DatePicker
                    allowClear
                    style={{ width: "100%" }}
                    value={
                      values.start_periodicity
                        ? dayjs(values.start_periodicity)
                        : null
                    }
                    onChange={(date) =>
                      setFieldValue(
                        "start_periodicity",
                        date ? date.toISOString() : null
                      )
                    }
                  />
                </div>
                <div className="w-1/2">
                  <Title level={5} className="text-[14px]">
                    End Periodicity
                  </Title>
                  <DatePicker
                    allowClear
                    style={{ width: "100%" }}
                    value={
                      values.end_periodicity
                        ? dayjs(values.end_periodicity)
                        : null
                    }
                    onChange={(date) =>
                      setFieldValue(
                        "end_periodicity",
                        date ? date.toISOString() : null
                      )
                    }
                  />
                </div>
              </div>

              {/* Email Subject */}
              <Title level={5} className="text-[14px] mt-4">
                Email Subject
              </Title>
              <Input
                placeholder="Enter email subject"
                value={values.email_subject}
                onChange={(e) => setFieldValue("email_subject", e.target.value)}
              />
              {touched.email_subject && errors.email_subject && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.email_subject}
                </p>
              )}

              {/* Email Body */}
              <Title level={5} className="text-[14px] mt-4">
                Email Body
              </Title>
              <Input.TextArea
                placeholder="Enter email body"
                value={values.email_body}
                onChange={(e) => setFieldValue("email_body", e.target.value)}
                rows={3}
              />
            </Form>
          </Modal>
        )}
      </Formik>
    </>
  );
};

export { AddReportScheduleModal };
